import React, { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import {
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Chip,
    IconButton,
    Box,
    Typography,
    Stack,
    Button,
    TextField,
    Link,
    Alert,
    Card,
    CardContent,
    InputAdornment,
    Tooltip,
    Badge,
    Skeleton,
    Pagination,
    Select,
    MenuItem,
    FormControl,
  
} from "@mui/material";
import { styled } from "@mui/material/styles";
import { 
    KeyboardArrowDown, 
    KeyboardArrowUp,
    Search as SearchIcon, 
    Refresh as RefreshIcon,
    FilterList as FilterIcon,
    Visibility as VisibilityIcon,
    TrendingUp as TrendingUpIcon,
    People as PeopleIcon,
    Campaign as CampaignIcon,
    LocalOffer as CouponIcon,
    CheckCircle as CheckCircleIcon,
    Cancel as CancelIcon,
} from "@mui/icons-material";
import { getCouponsListByCompany, getAdminCompanyId } from "../../../api/api";
import AdminHeader from "../../../components/AdminHeader/AdminHeader";
import { MAIN_URL } from "../../../api/api";
import { formatAdminDate } from "../../../utils/formatDate";
import { getCouponImageStyles } from "../../../constants/imageSizes";
import Cookies from "js-cookie";
import { getAdminNavLinks } from "../../../theme/adminNavLinks";
import { pageBackgroundSx } from "../../../theme/theme";
import { ADMIN_PAGE_GUIDES } from "../../../constants/adminPageGuides";

// Styled components for better visual appeal (same as Home page)
const StyledCard = styled(Card)(({ theme }) => ({
    borderRadius: 16,
    boxShadow: '0 4px 20px rgba(0, 0, 0, 0.08)',
    border: '1px solid rgba(0, 0, 0, 0.05)',
    transition: 'all 0.3s ease-in-out',
    '&:hover': {
        boxShadow: '0 8px 30px rgba(0, 0, 0, 0.12)',
        transform: 'translateY(-2px)',
    },
}));

const StyledTextField = styled(TextField)(({ theme }) => ({
    '& .MuiOutlinedInput-root': {
        borderRadius: 12,
        backgroundColor: 'rgba(255, 255, 255, 0.9)',
        '&:hover': {
            backgroundColor: 'rgba(255, 255, 255, 1)',
        },
        '&.Mui-focused': {
            backgroundColor: 'rgba(255, 255, 255, 1)',
        },
    },
}));


const Index = () => {
    const companyId = Cookies.get("admin_company_id");
    const navigate = useNavigate();
    const location = useLocation();
   
    const [openRows, setOpenRows] = useState({});
    const [searchTerm, setSearchTerm] = useState("");
    const [coupons, setCoupons] = useState([]);
    const [page, setPage] = useState(1);
    const [pagination, setPagination] = useState({
        current_page: 1,
        per_page: 10,
        total: 0,
        last_page: 1,
        from: 0,
        to: 0
    });
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [stats, setStats] = useState({
        totalUsers: 0,
        totalCoupons: 0,
        totalCampaigns: 0,
        validCoupons: 0,
        usedCoupons: 0,
        expiredCoupons: 0
    });

    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [allCoupons, setAllCoupons] = useState([]); // Store all coupons for filtering

    useEffect(() => {
        const fetchCompanyIdAndCoupons = async () => {
            // Try to get company ID from cookie or location state first
            let effectiveCompanyId = companyId || location.state?.admin_company_id;

            console.log("Cookie admin_company_id:", companyId);
            console.log("Location state admin_company_id:", location.state?.admin_company_id);
            console.log("Initial effective company ID:", effectiveCompanyId);

            // Fallback: If no company ID, try to fetch it using admin_id (like AdminHome does)
            if (!effectiveCompanyId) {
                const adminId = Cookies.get("admin_id");
                console.log("No company ID found, trying to fetch using admin_id:", adminId);
                
                if (adminId) {
                    try {
                        const response = await getAdminCompanyId(adminId);
                        effectiveCompanyId = response.data.company.id;
                        console.log("Fetched company ID from API:", effectiveCompanyId);
                        
                        // Set the cookie so we don't have to fetch again
                        Cookies.set("admin_company_id", effectiveCompanyId, { expires: 90 });
                    } catch (error) {
                        console.error("Failed to fetch company ID:", error);
                        setError("Company ID not found. Please login again.");
                        setLoading(false);
                        return;
                    }
                } else {
                    console.log("No admin_id found either");
                    setError("Company ID not found. Please login again.");
                    setLoading(false);
                    return;
                }
            }

            if (!effectiveCompanyId) {
                console.log("Still no company ID found after all attempts");
                setError("Company ID not found. Please login again.");
                setLoading(false);
                return;
            }

            // Now fetch the coupons
            setLoading(true);
            setError(null);
            
            try {
                console.log("Fetching coupons for company:", effectiveCompanyId);
                // When searching, fetch all data (use large per_page). Otherwise use pagination
                const fetchPerPage = searchTerm.trim() ? 10000 : rowsPerPage;
                const fetchPage = searchTerm.trim() ? 1 : page;
                const res = await getCouponsListByCompany(effectiveCompanyId, fetchPage, fetchPerPage);
                const rawData = res.data.data;
                const paginationData = res.data.pagination || {};

                // Convert object to array for processing
                const rows = Object.entries(rawData || {}).map(([mobile, row]) => {
                    const campaigns = row.Campaigns || [];
                    const firstCreated = campaigns.length
                        ? campaigns[0].Coupons?.[0]?.CouponCreated || "-"
                        : "-";

                    // Calculate valid coupons: not used AND not expired
                    const currentDate = new Date();
                    currentDate.setHours(0, 0, 0, 0); // Reset time to start of day for accurate comparison
                    
                    let validCount = 0;
                    let usedCount = 0;
                    let expiredCount = 0;
                    
                    campaigns.forEach(campaign => {
                        const coupons = campaign.Coupons || [];
                        coupons.forEach(coupon => {
                            const isUsed = coupon.Used === "1" || coupon.Used === 1;
                            const expiryDate = coupon.ExpiryDate ? new Date(coupon.ExpiryDate) : null;
                            const isExpired = expiryDate ? expiryDate < currentDate : false;
                            
                            if (isUsed) {
                                usedCount++;
                            } else if (isExpired) {
                                // Expired coupon: not used but expired
                                expiredCount++;
                            } else {
                                // Valid coupon: not used and not expired
                                validCount++;
                            }
                        });
                    });

                    return {
                        mobileNr: mobile,
                        userName: row.UserName || "",
                        campaigns,
                        firstCreated,
                        validCount,
                        usedCount,
                        expiredCount,
                        maxExpiryDate: row.MaxExpiryDate || null,
                        userCreatedDate: row.UserCreatedDate || null, // Add user creation date for sorting
                    };
                });

                // Sort by user creation date in descending order (newest first)
                rows.sort((a, b) => {
                    if (!a.userCreatedDate && !b.userCreatedDate) return 0;
                    if (!a.userCreatedDate) return 1;
                    if (!b.userCreatedDate) return -1;
                    return new Date(b.userCreatedDate) - new Date(a.userCreatedDate);
                });

                // Calculate statistics
                const totalCoupons = rows.reduce((sum, row) => sum + row.validCount + row.usedCount + row.expiredCount, 0);
                const validCoupons = rows.reduce((sum, row) => sum + row.validCount, 0);
                const usedCoupons = rows.reduce((sum, row) => sum + row.usedCount, 0);
                const expiredCoupons = rows.reduce((sum, row) => sum + row.expiredCount, 0);
                const totalCampaigns = new Set(
                    rows.flatMap(row => 
                        row.campaigns.map(c => c.CampaignName).filter(Boolean)
                    )
                ).size;

                // Note: Stats are calculated from current page only
                // For accurate total stats, you may need a separate stats endpoint
                setStats({
                    totalUsers: paginationData.total || rows.length,
                    totalCoupons,
                    totalCampaigns,
                    validCoupons,
                    usedCoupons,
                    expiredCoupons
                });

                setAllCoupons(rows); // Store all fetched coupons
                // Only set coupons directly if not searching (filter useEffect will handle search case)
                if (!searchTerm.trim()) {
                    setCoupons(rows);
                    setPagination(paginationData);
                }
            } catch (err) {
                console.error("Error loading coupons", err);
                const errorMessage = err.response?.data?.message || err.response?.data?.error || "Failed to load coupon data. Please try again.";
                const statusCode = err.response?.status;
                const debugInfo = statusCode ? ` (Status: ${statusCode})` : '';
                setError(errorMessage + debugInfo);
                setCoupons([]);
            } finally {
                setLoading(false);
            }
        };
        
        fetchCompanyIdAndCoupons();
    }, [companyId, location.state?.admin_company_id, page, rowsPerPage, searchTerm]);

    const toggleRow = (mobile) => {
        setOpenRows((prev) => ({ ...prev, [mobile]: !prev[mobile] }));
    };

    // Client-side filtering and pagination based on search term
    useEffect(() => {
        if (!searchTerm.trim()) {
            // If no search term, use backend pagination (data already paginated)
            // Don't re-paginate, just use what was fetched
            return;
        } else {
            // Filter coupons based on search term
            const searchLower = searchTerm.toLowerCase().trim();
            const filtered = allCoupons.filter(row => {
                // Search in mobile number
                const mobileMatch = row.mobileNr?.toLowerCase().includes(searchLower);
                // Search in user name
                const nameMatch = row.userName?.toLowerCase().includes(searchLower);
                // Search in campaign names
                const campaignMatch = row.campaigns?.some(campaign => 
                    campaign.CampaignName?.toLowerCase().includes(searchLower)
                );
                return mobileMatch || nameMatch || campaignMatch;
            });

            // Apply pagination to filtered results
            const offset = (page - 1) * rowsPerPage;
            const paginated = filtered.slice(offset, offset + rowsPerPage);
            setCoupons(paginated);
            setPagination({
                current_page: page,
                per_page: rowsPerPage,
                total: filtered.length,
                last_page: Math.ceil(filtered.length / rowsPerPage),
                from: filtered.length > 0 ? offset + 1 : 0,
                to: Math.min(offset + rowsPerPage, filtered.length)
            });
        }
    }, [searchTerm, allCoupons, page, rowsPerPage]);

    // Reset to page 1 when search term changes
    useEffect(() => {
        if (searchTerm.trim() && page !== 1) {
            setPage(1);
        }
    }, [searchTerm]);

    const handleRefresh = () => {
        window.location.reload();
    };

    const handleListAll = () => {
        setSearchTerm("");
        setPage(1);
    };

    // No need for client-side pagination - backend handles it
    const paginatedCoupons = Array.isArray(coupons) ? coupons : [];

    // Loading skeleton component
    const LoadingSkeleton = () => (
        <Box sx={{ width: "100%" }}>
            {[...Array(5)].map((_, index) => (
                <Skeleton key={index} variant="rectangular" height={60} sx={{ mb: 1 }} />
            ))}
        </Box>
    );

    // Empty state component
    const EmptyState = () => (
        <Box sx={{ textAlign: "center", py: { xs: 4, sm: 8 }, px: { xs: 2, sm: 4 } }}>
            <CouponIcon sx={{ fontSize: { xs: 60, sm: 80 }, color: "grey.400", mb: 2 }} />
            <Typography 
                variant="h6" 
                color="text.secondary" 
                gutterBottom
                sx={{ fontSize: { xs: '1rem', sm: '1.25rem' } }}
            >
                No Coupons Found
            </Typography>
            <Typography 
                variant="body2" 
                color="text.secondary"
                sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}
            >
                {searchTerm ? "Try adjusting your search criteria" : "No coupons have been created yet"}
            </Typography>
        </Box>
    );

    return (
            <Box sx={pageBackgroundSx}>
                <AdminHeader 
                    title="Coupon Reports"
                    icon={CouponIcon}
                    navigationLinks={getAdminNavLinks("/list")}
                    guide={ADMIN_PAGE_GUIDES.list}
                />

                {/* Main Content */}
                <Box sx={{ maxWidth: { xs: "100%", sm: "95%" }, mx: "auto", p: { xs: 0.5, sm: 1 } }}>
                    <StyledCard sx={{ mb: { xs: 1, sm: 2 } }}>
                        <CardContent sx={{ py: { xs: 1, sm: 1.5 }, px: { xs: 1, sm: 2 } }}>
                        <Stack 
                            direction={{ xs: 'column', md: 'row' }} 
                            spacing={{ xs: 2, sm: 2 }} 
                            alignItems={{ xs: 'stretch', md: 'center' }}
                        >
                            {/* Title */}
                            <Typography 
                                variant="h5" 
                                fontWeight="bold" 
                                color="primary" 
                                sx={{ 
                                    minWidth: { xs: 'auto', md: 200 },
                                    textAlign: { xs: 'center', md: 'left' }
                                }}
                            >
                                Coupon List
                           </Typography>

                            {/* Compact Statistics */}
                            <Stack 
                                direction="row" 
                                spacing={{ xs: 0.5, sm: 1 }} 
                                sx={{ 
                                    flex: 1, 
                                    flexWrap: 'wrap', 
                                    gap: { xs: 0.5, sm: 1 },
                                    justifyContent: { xs: 'center', md: 'flex-start' }
                                }}
                            >
                                <Chip 
                                    icon={<PeopleIcon />} 
                                    label={`${stats.totalUsers} Users`} 
                                    color="primary" 
                                    variant="outlined" 
                                    size="small"
                                    sx={{ fontSize: { xs: '0.7rem', sm: '0.75rem' } }}
                                />
                                <Chip 
                                    icon={<CouponIcon />} 
                                    label={`${stats.totalCoupons} Total`} 
                                    color="primary" 
                                    variant="outlined" 
                                    size="small"
                                    sx={{ fontSize: { xs: '0.7rem', sm: '0.75rem' } }}
                                />
                                <Chip 
                                    icon={<TrendingUpIcon />} 
                                    label={`${stats.validCoupons} Valid`} 
                                    color="primary" 
                                    variant="outlined" 
                                    size="small"
                                    sx={{ fontSize: { xs: '0.7rem', sm: '0.75rem' } }}
                                />
                                <Chip 
                                    icon={<CheckCircleIcon />} 
                                    label={`${stats.usedCoupons} Used`} 
                                    color="primary" 
                                    variant="outlined" 
                                    size="small"
                                    sx={{ fontSize: { xs: '0.7rem', sm: '0.75rem' } }}
                                />
                                <Chip 
                                    icon={<CancelIcon />} 
                                    label={`${stats.expiredCoupons} Expired`} 
                                    color="primary" 
                                    variant="outlined" 
                                    size="small"
                                    sx={{ fontSize: { xs: '0.7rem', sm: '0.75rem' } }}
                                />
                                <Chip 
                                    icon={<CampaignIcon />} 
                                    label={`${stats.totalCampaigns} Campaigns`} 
                                    color="primary" 
                                    variant="outlined" 
                                    size="small"
                                    sx={{ fontSize: { xs: '0.7rem', sm: '0.75rem' } }}
                                />
                            </Stack>

                            {/* Search Bar and Actions */}
                            <Stack 
                                direction={{ xs: 'column', sm: 'row' }} 
                                spacing={1} 
                                sx={{ 
                                    width: { xs: '100%', md: 'auto' },
                                    minWidth: { xs: '100%', md: 300 }
                                }}
                            >
                                <StyledTextField
                                    placeholder="Search mobile, name, campaign..."
                                    variant="outlined"
                                    size="small"
                                    sx={{ 
                                        width: { xs: '100%', md: 'auto' },
                                        minWidth: { xs: '100%', sm: 200, md: 250 }
                                    }}
                                    value={searchTerm}
                                    onChange={(e) => setSearchTerm(e.target.value)}
                                    InputProps={{
                                        startAdornment: (
                                            <InputAdornment position="start">
                                                <SearchIcon color="action" />
                                            </InputAdornment>
                                        ),
                                    }}
                                />

                                {/* Action Buttons */}
                                <Stack 
                                    direction="row" 
                                    spacing={1}
                                    justifyContent={{ xs: 'center', sm: 'flex-end' }}
                                >
                                    <Tooltip title="Clear Search">
                                        <IconButton 
                                            onClick={handleListAll} 
                                            disabled={!searchTerm}
                                            size="small"
                                        >
                                            <FilterIcon />
                                        </IconButton>
                                    </Tooltip>
                                    <Tooltip title="Refresh Data">
                                        <IconButton onClick={handleRefresh} color="primary" size="small">
                                            <RefreshIcon />
                                        </IconButton>
                                    </Tooltip>
                                </Stack>
                            </Stack>
                        </Stack>
                        </CardContent>
                </StyledCard>

                {/* Error State */}
                {error && (
                    <Alert 
                        severity="error" 
                        sx={{ mb: { xs: 1, sm: 2 } }} 
                        action={
                            <Button 
                                color="inherit" 
                                size="small" 
                                onClick={handleRefresh}
                                sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}
                            >
                                Retry
                            </Button>
                        }
                    >
                        {error}
                    </Alert>
                )}

                {/* Main Content */}
                <Card>
                    <CardContent sx={{ p: 0 }}>
                        {loading ? (
                            <Box sx={{ p: { xs: 1, sm: 2 } }}>
                                <LoadingSkeleton />
                            </Box>
                        ) : coupons.length === 0 ? (
                            <EmptyState />
                        ) : (
                            <>
                                {/* Table Header */}
                                <Box sx={{ p: { xs: 1, sm: 1.5 }, borderBottom: 1, borderColor: "divider" }}>
                                    <Stack 
                                        direction={{ xs: 'column', sm: 'row' }} 
                                        justifyContent="space-between" 
                                        alignItems={{ xs: 'flex-start', sm: 'center' }}
                                        spacing={1}
                                    >
                                        <Typography 
                                            variant="subtitle1" 
                                            fontWeight="bold"
                                            sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}
                                        >
                                            Coupon Details
                                        </Typography>
                                        <Badge badgeContent={coupons.length} color="primary">
                                            <VisibilityIcon color="action" />
                                        </Badge>
                    </Stack>
                                </Box>

                                {/* Table */}
                                <TableContainer sx={{ 
                                    maxHeight: { xs: 400, sm: 600 },
                                    overflow: 'auto',
                                    '&::-webkit-scrollbar': {
                                        width: '8px',
                                        height: '8px',
                                    },
                                    '&::-webkit-scrollbar-track': {
                                        background: '#f1f1f1',
                                        borderRadius: '4px',
                                    },
                                    '&::-webkit-scrollbar-thumb': {
                                        background: '#c1c1c1',
                                        borderRadius: '4px',
                                        '&:hover': {
                                            background: '#a8a8a8',
                                        },
                                    },
                                }}>
                                    <Table 
                                        size="small" 
                                        sx={{ 
                                            tableLayout: { xs: 'auto', md: 'fixed' },
                                            minWidth: 800
                                        }}
                                    >
                            <TableHead>
                                            <TableRow sx={{ bgcolor: "grey.100" }}>
                                                <TableCell sx={{ fontWeight: "bold", width: { xs: 'auto', md: 60 }, padding: { xs: "4px 8px", sm: "8px 16px" }, fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>Actions</TableCell>
                                                <TableCell sx={{ fontWeight: "bold", width: { xs: 'auto', md: 120 }, padding: { xs: "4px 8px", sm: "8px 16px" }, fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>Mobile No</TableCell>
                                                <TableCell sx={{ fontWeight: "bold", width: { xs: 'auto', md: 120 }, padding: { xs: "4px 8px", sm: "8px 16px" }, fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>User Name</TableCell>
                                                <TableCell sx={{ fontWeight: "bold", width: { xs: 'auto', md: 150 }, padding: { xs: "4px 8px", sm: "8px 16px" }, fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>Campaigns</TableCell>
                                                <TableCell sx={{ fontWeight: "bold", width: { xs: 'auto', md: 120 }, padding: { xs: "4px 8px", sm: "8px 16px" }, fontSize: { xs: '0.75rem', sm: '0.875rem' }, display: { xs: 'none', md: 'table-cell' } }}>First Created</TableCell>
                                                <TableCell sx={{ fontWeight: "bold", width: { xs: 'auto', md: 100 }, padding: { xs: "4px 8px", sm: "8px 16px" }, fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>Valid</TableCell>
                                                <TableCell sx={{ fontWeight: "bold", width: { xs: 'auto', md: 100 }, padding: { xs: "4px 8px", sm: "8px 16px" }, fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>Used</TableCell>
                                                <TableCell sx={{ fontWeight: "bold", width: { xs: 'auto', md: 120 }, padding: { xs: "4px 8px", sm: "8px 16px" }, fontSize: { xs: '0.75rem', sm: '0.875rem' }, display: { xs: 'none', lg: 'table-cell' } }}>Last Expiry</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                            {paginatedCoupons.map((row) => {
                                                const campaigns = row.campaigns || [];
                                    const firstCreated = campaigns.length
                                                    ? campaigns[0].Coupons?.[0]?.CouponCreated || "-"
                                        : "-";

                                                const visibleCampaigns = campaigns.slice(0, 3);
                                    const extraCount = campaigns.length - 3;

                                    return (
                                                    <React.Fragment key={row.mobileNr}>
                                            {/* Main Row */}
                                            <TableRow
                                                hover
                                                            onClick={() => toggleRow(row.mobileNr)}
                                                            sx={{ 
                                                                cursor: "pointer",
                                                                "&:hover": { bgcolor: "action.hover" }
                                                            }}
                                                        >
                                                            <TableCell sx={{ width: { xs: 'auto', md: 60 }, padding: { xs: "4px 8px", sm: "8px 16px" } }}>
                                                                <Tooltip title={openRows[row.mobileNr] ? "Collapse" : "Expand"}>
                                                                    <IconButton size="small" color="primary">
                                                                        {openRows[row.mobileNr] ? (
                                                            <KeyboardArrowUp />
                                                        ) : (
                                                            <KeyboardArrowDown />
                                                        )}
                                                    </IconButton>
                                                                </Tooltip>
                                                            </TableCell>
                                                            <TableCell sx={{ width: { xs: 'auto', md: 120 }, padding: { xs: "4px 8px", sm: "8px 16px" } }}>
                                                                <Link
                                                                    href={`/admin?mobile=${row.mobileNr}`}
                                                                    onClick={(e) => e.stopPropagation()}
                                                                    sx={{
                                                                        textDecoration: 'none',
                                                                        color: 'primary.main',
                                                                        fontWeight: 'medium',
                                                                        cursor: 'pointer',
                                                                        '&:hover': {
                                                                            textDecoration: 'underline',
                                                                            color: 'primary.dark'
                                                                        }
                                                                    }}
                                                                >
                                                                    <Typography variant="body2" fontWeight="medium" sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
                                                                        {row.mobileNr}
                                                                    </Typography>
                                                                </Link>
                                                            </TableCell>
                                                            <TableCell sx={{ width: { xs: 'auto', md: 120 }, padding: { xs: "4px 8px", sm: "8px 16px" } }}>
                                                                <Typography variant="body2" sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
                                                                    {row.userName || "N/A"}
                                                                </Typography>
                                                </TableCell>
                                                            <TableCell sx={{ width: { xs: 'auto', md: 150 }, padding: { xs: "4px 8px", sm: "8px 16px" } }}>
                                                                <Stack direction="row" spacing={0.5} flexWrap="wrap">
                                                                    {visibleCampaigns.map((c, idx) => (
                                                            <Chip
                                                                key={idx}
                                                                            label={c.CampaignName || "Single Coupon"}
                                                                size="small"
                                                                            color="primary"
                                                                            variant="outlined"
                                                                            sx={{ fontSize: { xs: '0.65rem', sm: '0.75rem' }, height: { xs: 20, sm: 24 } }}
                                                            />
                                                                    ))}
                                                    {extraCount > 0 && (
                                                        <Chip
                                                            label={`+${extraCount} more`}
                                                            size="small"
                                                                            color="default"
                                                                            variant="outlined"
                                                                            sx={{ fontSize: { xs: '0.65rem', sm: '0.75rem' }, height: { xs: 20, sm: 24 } }}
                                                        />
                                                    )}
                                                                </Stack>
                                                </TableCell>
                                                            <TableCell sx={{ width: { xs: 'auto', md: 120 }, padding: { xs: "4px 8px", sm: "8px 16px" }, display: { xs: 'none', md: 'table-cell' } }}>
                                                                <Typography variant="body2" color="text.secondary" sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
                                                    {formatAdminDate(firstCreated)}
                                                                </Typography>
                                                </TableCell>
                                                            <TableCell sx={{ width: { xs: 'auto', md: 100 }, padding: { xs: "4px 8px", sm: "8px 16px" } }}>
                                                                <Badge badgeContent={row.validCount} color="success">
                                                                    <CouponIcon color="action" sx={{ fontSize: { xs: '1rem', sm: '1.25rem' } }} />
                                                                </Badge>
                                                </TableCell>
                                                            <TableCell sx={{ width: { xs: 'auto', md: 100 }, padding: { xs: "4px 8px", sm: "8px 16px" } }}>
                                                                <Badge badgeContent={row.usedCount} color="warning">
                                                                    <TrendingUpIcon color="action" sx={{ fontSize: { xs: '1rem', sm: '1.25rem' } }} />
                                                                </Badge>
                                                </TableCell>
                                                            <TableCell sx={{ width: { xs: 'auto', md: 120 }, padding: { xs: "4px 8px", sm: "8px 16px" }, display: { xs: 'none', lg: 'table-cell' } }}>
                                                                <Typography variant="body2" color="text.secondary" sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
                                                                    {formatAdminDate(row.maxExpiryDate)}
                                                                </Typography>
                                                </TableCell>
                                            </TableRow>

                                            {/* Expanded Drilldown Rows */}
                                                        {openRows[row.mobileNr] && campaigns.map((campaign, campaignIndex) =>
                                                            (campaign.Coupons || []).map((coupon, couponIndex) => (
                                                                <TableRow
                                                                    key={`${campaignIndex}-${couponIndex}`}
                                                                    sx={{ bgcolor: "grey.50" }}
                                                                >
                                                                    {/* Actions Column - Empty but maintains alignment */}
                                                                    <TableCell sx={{ width: { xs: 'auto', md: 60 }, padding: { xs: "4px 8px", sm: "8px 16px" } }}></TableCell>
                                                                    
                                                                    {/* Mobile No Column - Empty but maintains alignment */}
                                                                    <TableCell sx={{ width: { xs: 'auto', md: 120 }, padding: { xs: "4px 8px", sm: "8px 16px" } }}></TableCell>
                                                                    
                                                                    {/* User Name Column - Empty but maintains alignment */}
                                                                    <TableCell sx={{ width: { xs: 'auto', md: 120 }, padding: { xs: "4px 8px", sm: "8px 16px" } }}></TableCell>
                                                                    
                                                                    {/* Campaign Column - Show campaign name only on first coupon */}
                                                                    <TableCell sx={{ width: { xs: 'auto', md: 150 }, padding: { xs: "4px 8px", sm: "8px 16px" } }}>
                                                                        {couponIndex === 0 && (
                                                                            <Typography variant="body2" fontWeight="medium" color="primary" sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
                                                                                {campaign.CampaignName || "Single Coupon"}
                                                                            </Typography>
                                                                        )}
                                                                    </TableCell>

                                                                    {/* First Created Column - Show coupon created date */}
                                                                    <TableCell sx={{ width: { xs: 'auto', md: 120 }, padding: { xs: "4px 8px", sm: "8px 16px" }, display: { xs: 'none', md: 'table-cell' } }}>
                                                                        <Typography variant="body2" color="text.secondary" sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
                                                                            {formatAdminDate(coupon.CouponCreated)}
                                                                        </Typography>
                                                                    </TableCell>

                                                                    {/* Valid Column - Show coupon image */}
                                                                    <TableCell sx={{ width: { xs: 'auto', md: 100 }, padding: { xs: "4px 8px", sm: "8px 16px" } }}>
                                                                        {/* <Tooltip title="Click to view full size"> */}
                                                                        <Box
                                                                            component="img"
                                                                            src={`${MAIN_URL}/images/${coupon.ImageUrl}`}
                                                                            alt="coupon"
                                                                            loading="lazy"
                                                                            decoding="async"
                                                                            sx={getCouponImageStyles('SMALL')}
                                                                        />
                                                                        {/* </Tooltip> */}
                                                                    </TableCell>

                                                                    {/* Used Column - Show used status */}
                                                                    <TableCell sx={{ width: { xs: 'auto', md: 100 }, padding: { xs: "4px 8px", sm: "8px 16px" } }}>
                                                                        <Chip 
                                                                            label={coupon.Used =="1" ? "Yes" : "No"} 
                                                                            color={coupon.Used =="1" ? "warning" : "success"}
                                                                            size="small"
                                                                            sx={{ fontSize: { xs: '0.65rem', sm: '0.75rem' }, height: { xs: 20, sm: 24 } }}
                                                                        />
                                                                    </TableCell>

                                                                    {/* Last Expiry Column - Show coupon expiry date */}
                                                                    <TableCell sx={{ width: { xs: 'auto', md: 120 }, padding: { xs: "4px 8px", sm: "8px 16px" }, display: { xs: 'none', lg: 'table-cell' } }}>
                                                                        <Typography variant="body2" color="text.secondary" sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
                                                                            {formatAdminDate(coupon.ExpiryDate)}
                                                                        </Typography>
                                                                    </TableCell>
                                                                </TableRow>
                                                            ))
                                                )}
                                        </React.Fragment>
                                    );
                                })}
                            </TableBody>
                        </Table>
                    </TableContainer>

                                {/* Pagination */}
                                <Box sx={{ p: { xs: 1, sm: 1.5 }, borderTop: 1, borderColor: "divider" }}>
                                    <Stack 
                                        direction={{ xs: 'column', sm: 'row' }} 
                                        justifyContent="space-between" 
                                        alignItems={{ xs: 'stretch', sm: 'center' }}
                                        spacing={{ xs: 2, sm: 2 }}
                                    >
                                        {/* Left side - Info and items per page */}
                                        <Stack 
                                            direction={{ xs: 'column', sm: 'row' }} 
                                            spacing={{ xs: 1, sm: 2 }}
                                            alignItems={{ xs: 'center', sm: 'center' }}
                                        >
                                            <Typography 
                                                variant="body2" 
                                                color="text.secondary"
                                                sx={{ 
                                                    fontSize: { xs: '0.75rem', sm: '0.875rem' },
                                                    textAlign: { xs: 'center', sm: 'left' }
                                                }}
                                            >
                                                {pagination.total > 0 ? (
                                                    `Showing ${pagination.from} to ${pagination.to} of ${pagination.total} entries`
                                                ) : (
                                                    'No entries found'
                                                )}
                                            </Typography>
                                            {pagination.total > 0 && (
                                                <Stack direction="row" spacing={1} alignItems="center">
                                                    <Typography variant="body2" color="text.secondary" sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
                                                        Items per page:
                                                    </Typography>
                                                    <FormControl size="small" sx={{ minWidth: 70 }}>
                                                        <Select
                                                            value={rowsPerPage}
                                                            onChange={(e) => {
                                                                setRowsPerPage(Number(e.target.value));
                                                                setPage(1); // Reset to first page when changing items per page
                                                            }}
                                                            sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' }, height: '32px' }}
                                                        >
                                                            <MenuItem value={10}>10</MenuItem>
                                                            <MenuItem value={25}>25</MenuItem>
                                                            <MenuItem value={50}>50</MenuItem>
                                                            <MenuItem value={100}>100</MenuItem>
                                                        </Select>
                                                    </FormControl>
                                                </Stack>
                                            )}
                                        </Stack>

                                        {/* Right side - Page selection */}
                                        {pagination.total > 0 && pagination.last_page > 1 && (
                                            <Stack 
                                                direction="row" 
                                                spacing={1}
                                                alignItems="center"
                                                justifyContent={{ xs: 'center', sm: 'flex-end' }}
                                                sx={{ flexWrap: 'wrap', gap: 1 }}
                                            >
                                                <Button
                                                    size="small"
                                                    variant="outlined"
                                                    disabled={page === 1}
                                                    onClick={() => setPage(1)}
                                                    sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' }, minWidth: 'auto', px: 1 }}
                                                >
                                                    First
                                                </Button>
                                                <Button
                                                    size="small"
                                                    variant="outlined"
                                                    disabled={page === 1}
                                                    onClick={() => setPage(page - 1)}
                                                    sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' }, minWidth: 'auto', px: 1 }}
                                                >
                                                    Prev
                                                </Button>
                                                
                                                {/* Page number buttons */}
                                                <Pagination
                                                    count={pagination.last_page}
                                                    page={page}
                                                    onChange={(event, value) => setPage(value)}
                                                    color="primary"
                                                    size="small"
                                                    showFirstButton
                                                    showLastButton
                                                    siblingCount={1}
                                                    boundaryCount={1}
                                                    sx={{
                                                        '& .MuiPaginationItem-root': {
                                                            fontSize: { xs: '0.75rem', sm: '0.875rem' },
                                                            minWidth: { xs: '28px', sm: '32px' },
                                                            height: { xs: '28px', sm: '32px' },
                                                        }
                                                    }}
                                                />
                                                
                                                <Button
                                                    size="small"
                                                    variant="outlined"
                                                    disabled={page >= pagination.last_page}
                                                    onClick={() => setPage(page + 1)}
                                                    sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' }, minWidth: 'auto', px: 1 }}
                                                >
                                                    Next
                                                </Button>
                                                <Button
                                                    size="small"
                                                    variant="outlined"
                                                    disabled={page >= pagination.last_page}
                                                    onClick={() => setPage(pagination.last_page)}
                                                    sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' }, minWidth: 'auto', px: 1 }}
                                                >
                                                    Last
                                                </Button>
                                                
                                                {/* Jump to page input */}
                                                <Stack direction="row" spacing={0.5} alignItems="center">
                                                    <Typography variant="body2" color="text.secondary" sx={{ fontSize: { xs: '0.7rem', sm: '0.75rem' }, whiteSpace: 'nowrap' }}>
                                                        Go to:
                                                    </Typography>
                                                    <TextField
                                                        type="number"
                                                        size="small"
                                                        value={page}
                                                        onChange={(e) => {
                                                            const value = parseInt(e.target.value);
                                                            if (value >= 1 && value <= pagination.last_page) {
                                                                setPage(value);
                                                            }
                                                        }}
                                                        inputProps={{
                                                            min: 1,
                                                            max: pagination.last_page,
                                                            style: { 
                                                                textAlign: 'center',
                                                                fontSize: '0.75rem',
                                                                padding: '4px 8px',
                                                                width: '50px'
                                                            }
                                                        }}
                                                        sx={{
                                                            width: '60px',
                                                            '& .MuiOutlinedInput-root': {
                                                                height: '32px',
                                                            }
                                                        }}
                                                    />
                                                    <Typography variant="body2" color="text.secondary" sx={{ fontSize: { xs: '0.7rem', sm: '0.75rem' } }}>
                                                        / {pagination.last_page}
                                                    </Typography>
                                                </Stack>
                                            </Stack>
                                        )}
                                </Stack>
                                </Box>
                            </>
                        )}
                        </CardContent>
                    </Card>
                </Box>
            </Box>
    );
};

export default Index;
